import { ReceiptFilters } from './ReceiptFilters';
import { DigitalAssetMaterials, assetCategories } from './DigitalAssetMaterials';
import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, post, useData, useSession } from '@yingbaba/api';
import {
  Button,
  Modal,
  Notice,
  PageTitle,
  ErrorNotice,
  Status,
  Empty,
  ButtonLink,
  Select,
  Field,
} from '@yingbaba/ui';
import {
  money,
  paymentFactAmount,
  datetime,
  beijingTimestamp,
  parseMemberMoney,
  memberCommandKey,
  clearMemberCommand,
} from '@yingbaba/utils';
import type {
  DigitalPublisher,
  DigitalAssetView,
  DigitalPayoutAccount,
  DigitalWithdrawal,
  DigitalReceipt,
  DigitalCopyrightCaseView,
  Page,
} from '@yingbaba/types';

type Dialog =
  | { kind: 'publisher'; value: DigitalPublisher }
  | { kind: 'asset'; value: DigitalAssetView }
  | { kind: 'account-review' | 'account-secret'; value: DigitalPayoutAccount }
  | { kind: 'withdraw'; value: DigitalWithdrawal }
  | { kind: 'refund' | 'retry'; value: DigitalReceipt }
  | { kind: 'copyright'; value: DigitalCopyrightCaseView };
const label: Record<string, string> = {
  pending: '待处理',
  active: '有效',
  suspended: '已暂停',
  rejected: '已拒绝',
  submitted: '待审核',
  approved: '已批准',
  draft: '草稿',
  published: '已上架',
  requested: '待审核',
  processing: '出款处理中',
  paid: '已支付',
  cancelled: '已取消',
  review: '到账待核查',
  fulfilled: '已签发授权',
  refunded: '已登记退款',
  received: '通知待受理',
  held: '版权核查中',
  dismissed: '已解除',
  upheld: '维持处理',
};
export function DigitalManagement() {
  const session = useSession();
  return <DigitalAdminScope key={session.data?.account.id || ''} />;
}
function DigitalAdminScope() {
  const session = useSession();
  const role = session.data?.account.platform_role || '';
  const review = ['super_admin', 'reviewer', 'operator'].includes(role);
  const finance = ['super_admin', 'finance'].includes(role);
  const audit = role === 'auditor';
  const directory = ['super_admin', 'operator', 'auditor'].includes(role);
  const allowed = review || finance || audit;
  const [params, setParams] = useSearchParams();
  const pendingParams = useRef(new URLSearchParams(params));
  useEffect(() => {
    pendingParams.current = new URLSearchParams(params);
  }, [params]);
  function updateParams(values: Record<string, string>) {
    const next = new URLSearchParams(pendingParams.current);
    for (const [key, value] of Object.entries(values))
      value ? next.set(key, value) : next.delete(key);
    pendingParams.current = next;
    setParams(next);
  }
  const allowedTabs = [
    'publishers',
    ...(review || audit ? ['assets', 'copyright'] : []),
    ...(finance || audit ? ['receipts', 'accounts', 'withdrawals'] : []),
  ];
  const requestedTab = params.get('tab') || '';
  const tab = allowedTabs.includes(requestedTab)
    ? requestedTab
    : finance && !review
      ? 'receipts'
      : 'publishers';
  const page = Math.min(10000, Math.max(1, Math.floor(Number(params.get('page')) || 1)));
  const queue = ['publishers', 'assets', 'copyright'].includes(tab);
  const queueStates =
    tab === 'publishers'
      ? ['pending', 'active', 'suspended', 'rejected']
      : tab === 'copyright'
        ? ['received', 'held', 'dismissed', 'upheld']
        : ['submitted', 'draft', 'approved', 'rejected'];
  const queueStatus = queueStates.includes(params.get('status') || '') ? params.get('status')! : '';
  const query = params.get('q') || '';
  const [search, setSearch] = useState(query);
  useEffect(() => setSearch(query), [query]);
  const category = Object.hasOwn(assetCategories, params.get('category') || '')
    ? params.get('category')!
    : '';
  const organization = params.get('organization_id') || '';
  const queueQuery = `&q=${encodeURIComponent(query)}&status=${queueStatus}&organization_id=${encodeURIComponent(organization)}`;
  const filter = ['pending', 'review', 'fulfilled', 'refunded'].includes(params.get('status') || '')
    ? params.get('status')!
    : '';
  const orderID = params.get('order_id') || '';
  const [orderQuery, setOrderQuery] = useState(orderID);
  useEffect(() => setOrderQuery(orderID), [orderID]);
  function setPage(value: number) {
    updateParams({ page: value > 1 ? String(value) : '' });
  }
  function setFilter(value: string) {
    updateParams({ status: value, page: '' });
  }
  function searchOrder(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = orderQuery.trim();
    if (value && !/^[0-9A-HJKMNP-TV-Z]{26}$/.test(value)) {
      setError(new Error('请填写完整的订单编号'));
      return;
    }
    setError(null);
    updateParams({ order_id: value, page: '' });
  }
  const qc = useQueryClient();
  const suppliers = useData<Page<DigitalPublisher>>(
    `/admin/digital/publishers?page=${page}${queueQuery}`,
    allowed && tab === 'publishers',
  );
  const assets = useData<Page<DigitalAssetView>>(
    `/admin/digital/assets?page=${page}${queueQuery}&category=${category}`,
    (review || audit) && tab === 'assets',
  );
  const accounts = useData<DigitalPayoutAccount[]>(
    '/admin/digital/payout-accounts',
    (finance || audit) && tab === 'accounts',
  );
  const withdrawals = useData<Page<DigitalWithdrawal>>(
    `/admin/digital/withdrawals?page=${page}`,
    (finance || audit) && tab === 'withdrawals',
  );
  const receipts = useData<Page<DigitalReceipt>>(
    `/admin/digital/receipts?page=${page}&status=${encodeURIComponent(filter)}&order_id=${encodeURIComponent(orderID)}`,
    (finance || audit) && tab === 'receipts',
    30_000,
  );
  const cases = useData<Page<DigitalCopyrightCaseView>>(
    `/admin/digital/copyright-cases?page=${page}${queueQuery}`,
    (review || audit) && tab === 'copyright',
  );
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const [message, setMessage] = useState('');
  const [secret, setSecret] = useState<Record<string, string> | null>(null);
  const refresh = () =>
    qc.invalidateQueries({
      predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].includes('/digital/'),
    });
  function open(d: Dialog) {
    setDialog(d);
    setError(null);
    setSecret(null);
  }
  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!dialog || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    const f = new FormData(e.currentTarget);
    const str = (k: string) => String(f.get(k) || '').trim();
    const proof = { password: str('password'), mfa_code: str('mfa_code') };
    const reason = str('reason');
    let scope = '';
    try {
      switch (dialog.kind) {
        case 'publisher':
          await post(`/admin/digital/publishers/${dialog.value.id}/review`, {
            status: str('status'),
            reason,
          });
          break;
        case 'asset':
          await post(`/admin/digital/assets/${dialog.value.asset.id}/review`, {
            revision_id: dialog.value.release.id,
            approve: str('decision') === 'approve',
            technical_checked: f.get('technical') === 'on',
            rights_checked: f.get('rights') === 'on',
            preview_checked: f.get('preview') === 'on',
            reason,
          });
          break;
        case 'account-review':
          await post(`/admin/digital/payout-accounts/${dialog.value.id}/review`, {
            status: str('status'),
            reason,
          });
          break;
        case 'account-secret':
          setSecret(
            await post<Record<string, string>>(
              `/admin/digital/payout-accounts/${dialog.value.id}/sensitive`,
              { reauthentication: proof, reason },
            ),
          );
          setDialog(null);
          setMessage('敏感查看已记入审计；使用后请关闭，不要复制到公开渠道。');
          return;
        case 'withdraw': {
          const w = dialog.value;
          const action = str('action');
          const payload = {
            action,
            expected_version: w.version,
            tax_minor: str('tax') ? parseMemberMoney(str('tax')) : 0,
            tax_evidence: str('tax_evidence'),
            reference: str('reference'),
            net_minor: w.net_minor,
            currency: 'CNY',
            paid_at: str('paid_at') ? beijingTimestamp(str('paid_at')) : undefined,
            reason,
          };
          scope = `digital-payout-action:${session.data?.account.id}:${w.id}`;
          await post(`/admin/digital/withdrawals/${w.id}/action`, {
            ...payload,
            reauthentication: proof,
            idempotency_key: await memberCommandKey(scope, payload),
          });
          break;
        }
        case 'refund': {
          const payload = {
            amount_minor: dialog.value.amount_minor,
            currency: dialog.value.currency,
            reference: str('reference'),
            refunded_at: beijingTimestamp(str('paid_at')),
            reason,
          };
          scope = `digital-refund:${session.data?.account.id}:${dialog.value.id}`;
          await post(`/admin/digital/receipts/${dialog.value.id}/refund-record`, {
            ...payload,
            idempotency_key: await memberCommandKey(scope, payload),
          });
          break;
        }
        case 'retry':
          await post(`/admin/digital/receipts/${dialog.value.id}/retry`, { reason });
          break;
        case 'copyright':
          await post(`/admin/digital/copyright-cases/${dialog.value.id}/action`, {
            action: str('action'),
            expected_version: dialog.value.version,
            reason,
          });
          break;
      }
      if (scope) clearMemberCommand(scope);
      setDialog(null);
      setMessage(
        dialog.kind === 'asset'
          ? '素材审核结果已保存。'
          : dialog.kind === 'publisher'
            ? '供稿资格处理结果已保存。'
            : '处理结果已保存。',
      );
      await refresh();
    } catch (e) {
      setError(e);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  const current =
    tab === 'publishers'
      ? suppliers
      : tab === 'assets'
        ? assets
        : tab === 'withdrawals'
          ? withdrawals
          : tab === 'receipts'
            ? receipts
            : tab === 'copyright'
              ? cases
              : null;
  if (!allowed) return <Notice>当前运营角色没有数字资源管理权限。</Notice>;
  return (
    <div className="admin-digital-page">
      <PageTitle
        title="数字资源管理"
        description="审核数字素材与供稿资格，处理版权、收款和提现。"
      />
      <nav className="tabs admin-section-tabs" aria-label="数字资源分类">
        {[
          ['publishers', '供稿资格', true],
          ['assets', '资源审核', review || audit],
          ['copyright', '版权案件', review || audit],
          ['receipts', '收款与退款', finance || audit],
          ['accounts', '同名收款账户', finance || audit],
          ['withdrawals', '提现审核', finance || audit],
        ]
          .filter((x) => x[2])
          .map(([k, name]) => (
            <button
              type="button"
              key={String(k)}
              className={tab === k ? 'active' : ''}
              aria-current={tab === k ? 'page' : undefined}
              onClick={() => {
                pendingParams.current = new URLSearchParams({ tab: String(k) });
                setParams(pendingParams.current);
                setError(null);
                setSecret(null);
              }}
            >
              {name}
            </button>
          ))}
      </nav>
      <section className="admin-data-panel">
        {queue && (
          <form
            className={`digital-review-filters ${tab === 'assets' ? 'with-category' : ''}`}
            role="search"
            aria-label={
              tab === 'assets'
                ? '查找数字素材'
                : tab === 'copyright'
                  ? '查找版权案件'
                  : '查找供稿资格'
            }
            onSubmit={(e) => {
              e.preventDefault();
              updateParams({ q: search.trim(), page: '' });
            }}
          >
            <input
              className="input"
              aria-label="审核关键词"
              maxLength={200}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                tab === 'assets'
                  ? '搜索素材或供稿方'
                  : tab === 'copyright'
                    ? '搜索案件、素材或组织'
                    : '搜索主体或组织'
              }
            />
            <Select
              aria-label="审核状态"
              value={queueStatus}
              onChange={(v) => updateParams({ status: v, page: '' })}
            >
              <option value="">全部状态</option>
              {queueStates.map((s) => (
                <option value={s} key={s}>
                  {s === 'pending' ? '待审核' : s === 'approved' ? '已通过' : label[s]}
                </option>
              ))}
            </Select>
            {tab === 'assets' && (
              <Select
                aria-label="素材类型"
                value={category}
                onChange={(v) => updateParams({ category: v, page: '' })}
              >
                <option value="">全部类型</option>
                {Object.entries(assetCategories).map(([k, name]) => (
                  <option value={k} key={k}>
                    {name}
                  </option>
                ))}
              </Select>
            )}
            <Button type="submit" variant="secondary">
              搜索
            </Button>
          </form>
        )}
        {queue && organization && (
          <Notice>
            正在查看指定组织的记录。{' '}
            {directory && <Link to={`/organizations/${organization}`}>查看组织</Link>}{' '}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => updateParams({ organization_id: '', page: '' })}
            >
              查看全部组织
            </Button>
          </Notice>
        )}
        <ErrorNotice error={error} />
        {message && <Notice>{message}</Notice>}
        {secret && (
          <section className="card" style={{ padding: 18, overflowWrap: 'anywhere' }}>
            <h2>受控收款资料</h2>
            <p>{secret.legal_name}</p>
            <p>
              {secret.bank_name} {secret.number}
            </p>
            <Button variant="secondary" onClick={() => setSecret(null)}>
              关闭敏感信息
            </Button>
          </section>
        )}
        <ErrorNotice error={current?.error || (tab === 'accounts' ? accounts.error : null)} />
        {current?.isError && (
          <Button variant="secondary" onClick={() => void current.refetch()}>
            重新读取记录
          </Button>
        )}
        {current?.isLoading && <p>正在读取记录…</p>}
        {tab === 'publishers' &&
          suppliers.data?.items.map((p) => (
            <section className="list-item admin-record" key={p.id}>
              <div className="grow">
                <strong>
                  {p.legal_name} · {label[p.status]}
                </strong>
                <p>
                  佣金 {p.commission_bps / 100}% · 结算观察期 {p.settlement_days} 天
                </p>
                <p className="small muted">
                  主体 {p.organization_id} · 协议 {p.agreement_version}
                </p>
                <p>{p.review_reason}</p>
              </div>
              {review && (
                <Button onClick={() => open({ kind: 'publisher', value: p })}>审核资格</Button>
              )}
            </section>
          ))}
        {tab === 'assets' &&
          assets.data?.items.map((v) => (
            <section className="digital-review-card" key={v.asset.id}>
              <div className="digital-review-heading">
                <div>
                  <h2>{v.release.title}</h2>
                  <p className="small muted">
                    {assetCategories[v.release.category] || v.release.category} · 版本{' '}
                    {v.release.version} · 权利人 {v.rights.holder_name}
                  </p>
                </div>
                <span className="tag">
                  {v.release.status === 'approved' ? '已通过' : label[v.release.status]}
                </span>
              </div>
              <p className="digital-review-description">{v.release.description}</p>
              <div className="digital-review-actions">
                {directory && (
                  <Link className="small link" to={`/organizations/${v.asset.organization_id}`}>
                    查看供稿组织
                  </Link>
                )}
                <span className="small muted">
                  {v.files.length} 个文件 ·{' '}
                  {v.release.submitted_at
                    ? `提交于 ${datetime(v.release.submitted_at)}`
                    : `更新于 ${datetime(v.release.updated_at)}`}
                </span>
                <Button
                  variant={review && v.release.status === 'submitted' ? 'primary' : 'secondary'}
                  onClick={() => open({ kind: 'asset', value: v })}
                >
                  {review && v.release.status === 'submitted' ? '查看并审核' : '查看材料'}
                </Button>
              </div>
            </section>
          ))}
        {tab === 'accounts' &&
          accounts.data?.map((v) => (
            <section className="list-item admin-record" key={v.id}>
              <div className="grow">
                <strong>{v.legal_name}</strong>
                <p>
                  {v.kind} · {v.bank_name} {v.masked_number} · {label[v.status]}
                </p>
                <p>{v.review_reason}</p>
              </div>
              {finance && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => open({ kind: 'account-secret', value: v })}
                  >
                    审计查看账号
                  </Button>
                  {v.status === 'pending' && (
                    <Button onClick={() => open({ kind: 'account-review', value: v })}>
                      同名核验
                    </Button>
                  )}
                </>
              )}
            </section>
          ))}
        {tab === 'withdrawals' && (
          <>
            <Notice>
              审核与执行必须由不同财务人员完成。“开始处理”只锁定出款操作，不会自动向银行转账。结果未知必须保持处理中，不能重新打另一笔。
            </Notice>
            {withdrawals.data?.items.map((w) => (
              <section className="list-item admin-record" key={w.id}>
                <div className="grow">
                  <strong>
                    {money(w.amount_minor)} · {label[w.status]}
                  </strong>
                  <p>
                    {w.legal_name} · {w.masked_account}
                  </p>
                  <p>
                    扣税 {money(w.tax_minor)} · 净到账 {money(w.net_minor)} · 版本 {w.version}
                  </p>
                  <p className="small muted">原打款号 {w.merchant_transfer_no}</p>
                  <p>{w.reason}</p>
                </div>
                {finance && ['requested', 'approved', 'processing'].includes(w.status) && (
                  <Button onClick={() => open({ kind: 'withdraw', value: w })}>处理提现</Button>
                )}
              </section>
            ))}
          </>
        )}
        {tab === 'receipts' && (
          <>
            <div className="admin-section-heading">
              <div>
                <h2>数字资源收款与退款</h2>
                <p className="muted">核对真实到账，复核授权签发异常与退款凭证。</p>
              </div>
              <ButtonLink to="/payment-settings?business=digital" variant="secondary">
                支付方式
              </ButtonLink>
            </div>
            <ReceiptFilters
              states={{
                pending: '等待自动签发',
                review: '到账待核查',
                fulfilled: '已签发授权',
                refunded: '已登记退款',
              }}
              status={filter}
              onStatus={setFilter}
              statusLabel="到账状态"
              orderLabel="订单编号"
              search={orderQuery}
              onSearch={setOrderQuery}
              onSubmit={searchOrder}
            />
            {receipts.data?.items.map((r) => (
              <section className="list-item admin-record" key={r.id}>
                <div className="grow">
                  <div className="admin-record-heading">
                    <strong>{paymentFactAmount(r.amount_minor, r.currency)}</strong>
                    <Status
                      value={
                        r.fulfillment_status === 'review'
                          ? 'pending'
                          : r.fulfillment_status === 'fulfilled'
                            ? 'completed'
                            : r.fulfillment_status
                      }
                      label={
                        r.fulfillment_status === 'pending'
                          ? '等待自动签发'
                          : label[r.fulfillment_status]
                      }
                    />
                  </div>
                  <dl className="admin-record-meta">
                    <div>
                      <dt>支付渠道</dt>
                      <dd>{r.provider === 'wechat' ? '微信支付' : '支付宝'}</dd>
                    </div>
                    <div>
                      <dt>订单编号</dt>
                      <dd>{r.order_id}</dd>
                    </div>
                    <div>
                      <dt>渠道流水</dt>
                      <dd>{r.trade_no}</dd>
                    </div>
                    <div>
                      <dt>实际付款</dt>
                      <dd>{r.paid_at ? datetime(r.paid_at) : '待渠道补充时间'}</dd>
                    </div>
                  </dl>
                  {r.currency !== 'CNY' && (
                    <p>到账币种异常，请通过原渠道核实；此处仅支持人民币退款登记。</p>
                  )}
                  {r.refunded_at && <p>退款登记完成：{datetime(r.refunded_at)}</p>}
                  {r.failure_code && (
                    <p>
                      核查原因：
                      {(
                        {
                          DIGITAL_PAYMENT_MISMATCH: '到账金额或币种与订单不符',
                          DIGITAL_PAYMENT_TIME_INVALID: '付款时间缺失或不在支付有效期内',
                          DIGITAL_ORDER_NOT_PAYABLE: '原订单已取消或结束',
                          COPYRIGHT_HOLD: '资源处于版权核查中',
                          DIGITAL_OWNER_INACTIVE: '购买主体已停用或注销中',
                          PUBLISHER_NOT_ACTIVE: '供稿资格未通过或已暂停',
                          PUBLISHER_VERIFICATION_CHANGED: '供稿主体认证发生变化',
                          PROVIDER_REFUND_DETECTED: '渠道提示已退款，请核实真实退款凭证',
                        } as Record<string, string>
                      )[r.failure_code] || r.failure_code}
                    </p>
                  )}
                </div>
                {finance && r.fulfillment_status !== 'refunded' && (
                  <div className="admin-record-actions">
                    {['review', 'pending'].includes(r.fulfillment_status) && (
                      <Button variant="secondary" onClick={() => open({ kind: 'retry', value: r })}>
                        复核授权签发条件
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      disabled={r.currency !== 'CNY'}
                      onClick={() => open({ kind: 'refund', value: r })}
                    >
                      登记已完成退款
                    </Button>
                  </div>
                )}
              </section>
            ))}
          </>
        )}
        {tab === 'copyright' &&
          cases.data?.items.map((c) => (
            <section className="card" key={c.id} style={{ padding: 18, marginTop: 12 }}>
              <div className="digital-review-heading">
                <h2>{c.asset_title || '数字素材版权通知'}</h2>
                <span className="tag">{label[c.status]}</span>
              </div>
              <p className="small muted">
                案件 {c.id} · {datetime(c.created_at)}
              </p>
              <p className="digital-review-description">{c.claim}</p>
              <details className="digital-case-record">
                <summary>证据与处理记录</summary>
                <p>初步证据：{c.evidence}</p>
                <p>反通知：{c.counter_statement || '尚未提交'}</p>
                <p>处理依据：{c.decision || '尚未处理'}</p>
              </details>
              {review && ['received', 'held'].includes(c.status) && (
                <Button onClick={() => open({ kind: 'copyright', value: c })}>处理通知</Button>
              )}
            </section>
          ))}
        {((current?.isSuccess && !current.data.items.length) ||
          (tab === 'accounts' && accounts.isSuccess && !accounts.data.length)) && (
          <Empty
            title={
              tab === 'receipts'
                ? '暂无匹配的收款记录'
                : queue
                  ? '没有匹配的记录'
                  : '暂无待查看的记录'
            }
            description={
              tab === 'receipts'
                ? '可调整到账状态或订单编号。真实到账后，记录会显示在这里。'
                : queue
                  ? '可调整关键词、审核状态或素材类型。'
                  : '收到相关申请或内容后会显示在这里。'
            }
            action={
              queue && (query || queueStatus || category || organization || page > 1) ? (
                <Button
                  variant="secondary"
                  onClick={() =>
                    updateParams({ q: '', status: '', category: '', organization_id: '', page: '' })
                  }
                >
                  查看全部记录
                </Button>
              ) : undefined
            }
          />
        )}
        {current?.isSuccess && current.data.total > current.data.page_size && (
          <div className="admin-pagination">
            <Button variant="secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>
              上一页
            </Button>
            <span>
              第 {page} 页，共 {current.data.total} 条
            </span>
            <Button
              variant="secondary"
              disabled={page * current.data.page_size >= current.data.total}
              onClick={() => setPage(page + 1)}
            >
              下一页
            </Button>
          </div>
        )}
      </section>
      {dialog && (
        <Modal
          open
          busy={busy}
          title={
            {
              publisher: '审核供稿资格',
              asset:
                review && dialog.kind === 'asset' && dialog.value.release.status === 'submitted'
                  ? '审核数字素材'
                  : '数字素材材料',
              'account-review': '核验同名收款账户',
              'account-secret': '查看受控收款资料',
              withdraw: '处理数字资源提现',
              refund: '登记渠道已完成的全额退款',
              retry: '复核授权签发条件',
              copyright: '处理版权案件',
            }[dialog.kind]
          }
          onOpenChange={(o) => {
            if (!o && !busy) setDialog(null);
          }}
          footer={
            dialog.kind === 'asset' &&
            (!review || dialog.value.release.status !== 'submitted') ? undefined : (
              <Button type="submit" form="admin-digital-dialog" loading={busy}>
                {dialog.kind === 'asset'
                  ? '保存审核结果'
                  : dialog.kind === 'copyright'
                    ? '保存案件处理结果'
                    : '确认处理'}
              </Button>
            )
          }
        >
          {dialog.kind === 'asset' && <DigitalAssetMaterials value={dialog.value} />}
          {(dialog.kind !== 'asset' || (review && dialog.value.release.status === 'submitted')) && (
            <form id="admin-digital-dialog" onSubmit={submit} style={{ display: 'grid', gap: 14 }}>
              {(dialog.kind === 'publisher' || dialog.kind === 'account-review') && (
                <>
                  <p>{dialog.value.legal_name}</p>
                  <Field label="处理结果">
                    <Select name="status">
                      {dialog.kind === 'publisher' ? (
                        <>
                          <option value="active">批准/恢复供稿资格</option>
                          <option value="rejected">拒绝</option>
                          <option value="suspended">暂停供稿</option>
                        </>
                      ) : (
                        <>
                          <option value="approved">通过同名与账户类型核验</option>
                          <option value="rejected">拒绝</option>
                        </>
                      )}
                    </Select>
                  </Field>
                </>
              )}
              {dialog.kind === 'asset' && (
                <>
                  <h3>审核决定</h3>
                  <Field label="决定">
                    <Select name="decision">
                      <option value="reject">驳回</option>
                      <option value="approve">批准上架</option>
                    </Select>
                  </Field>
                  <label className="digital-review-check">
                    <input type="checkbox" name="technical" />
                    <span>核验原文件技术质量、类型、描述、依赖及安全</span>
                  </label>
                  <label className="digital-review-check">
                    <input type="checkbox" name="rights" />
                    <span>核验原始权利、第三方许可及所售再许可范围</span>
                  </label>
                  <label className="digital-review-check">
                    <input type="checkbox" name="preview" />
                    <span>确认独立预览不泄漏原件，并有水印或试听保护</span>
                  </label>
                </>
              )}
              {dialog.kind === 'withdraw' && (
                <>
                  <p>
                    原打款号 {dialog.value.merchant_transfer_no}；申请{' '}
                    {money(dialog.value.amount_minor)}；净到账 {money(dialog.value.net_minor)}
                  </p>
                  <Field label="操作">
                    <Select name="action">
                      {dialog.value.status === 'requested' && (
                        <>
                          <option value="approve">审核批准</option>
                          <option value="reject">驳回并释放占用</option>
                        </>
                      )}
                      {dialog.value.status === 'approved' && (
                        <>
                          <option value="start">开始出款核验（不自动转账）</option>
                          <option value="reject">拒绝尚未执行的提现</option>
                        </>
                      )}
                      {dialog.value.status === 'processing' && (
                        <>
                          <option value="paid">依据真实凭证确认完成</option>
                          <option value="failed_verified">经核查原打款确未转出</option>
                        </>
                      )}
                    </Select>
                  </Field>
                  {dialog.value.status === 'requested' && (
                    <>
                      <label className="field">
                        实际适用扣税金额（元，无代扣填空）
                        <input className="input" name="tax" inputMode="decimal" />
                      </label>
                      <label className="field">
                        扣税判断及凭证
                        <input className="input" name="tax_evidence" maxLength={1000} />
                      </label>
                    </>
                  )}
                  {dialog.value.status === 'processing' && (
                    <>
                      <label className="field">
                        银行/渠道真实结果凭证号
                        <input
                          className="input"
                          name="reference"
                          required
                          minLength={6}
                          maxLength={120}
                        />
                      </label>
                      <label className="field">
                        实际支付时间（北京时间；未转出可留空）
                        <input className="input" name="paid_at" type="datetime-local" />
                      </label>
                    </>
                  )}
                  <Notice>
                    处理中不能按超时认定失败。已完成出款、扣税及退款须分别以事实入账。
                  </Notice>
                </>
              )}
              {dialog.kind === 'refund' && (
                <>
                  <Notice>
                    只登记已在渠道实际完成的全额退款，不发起转账。对应下载许可停止，未付收益反冲，已付部分记待追回。
                  </Notice>
                  <p>
                    {money(dialog.value.amount_minor)} · {dialog.value.trade_no}
                  </p>
                  <label className="field">
                    实际退款凭证号
                    <input
                      className="input"
                      name="reference"
                      required
                      minLength={6}
                      maxLength={120}
                    />
                  </label>
                  <label className="field">
                    实际退款时间（北京时间）
                    <input className="input" name="paid_at" type="datetime-local" required />
                  </label>
                </>
              )}
              {dialog.kind === 'copyright' && (
                <>
                  <div className="digital-case-record">
                    <strong>{dialog.value.asset_title || '数字素材版权通知'}</strong>
                    <p>
                      案件 {dialog.value.id} · 当前版本 {dialog.value.version}
                    </p>
                    <p className="prose">{dialog.value.claim}</p>
                    <details>
                      <summary>查看双方证据与处理记录</summary>
                      <p>初步证据：{dialog.value.evidence}</p>
                      <p>反通知：{dialog.value.counter_statement || '尚未提交'}</p>
                      <p>已有处理依据：{dialog.value.decision || '尚未处理'}</p>
                    </details>
                  </div>
                  <Field label="处理决定">
                    <Select name="action">
                      {dialog.value.status === 'received' ? (
                        <>
                          <option value="hold">受理并暂停销售/下载、冻结相关收益</option>
                          <option value="reject">缺乏受理依据，拒绝通知</option>
                        </>
                      ) : (
                        <>
                          <option value="dismiss">依据证据解除措施</option>
                          <option value="uphold">维持措施，逐单处理售后</option>
                        </>
                      )}
                    </Select>
                  </Field>
                </>
              )}
              <label className="field">
                核验依据与说明
                <textarea
                  name="reason"
                  required
                  minLength={dialog.kind === 'copyright' ? 10 : 6}
                  maxLength={1000}
                />
              </label>
              {(dialog.kind === 'withdraw' || dialog.kind === 'account-secret') && (
                <>
                  <label className="field">
                    当前管理员登录密码
                    <input
                      className="input"
                      name="password"
                      required
                      type="password"
                      autoComplete="current-password"
                    />
                  </label>
                  <label className="field">
                    双因素验证码/恢复码（已开启时必填）
                    <input
                      className="input"
                      name="mfa_code"
                      maxLength={32}
                      autoComplete="one-time-code"
                    />
                  </label>
                </>
              )}
              <ErrorNotice error={error} />
            </form>
          )}
        </Modal>
      )}
    </div>
  );
}
